// store/slices/workflowSlice.js
import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Core workflow data
  nodes: [],
  edges: [],

  // UI state
  selectedElement: null,
  isOpenPanel: false,
  selectedPanelData: {},

  // List data for sidebar
  listData: {
    tasks: [],
    subTasks: [],
    milestones: [],
    outcomes: [],
  },

  // Filter state
  filterPayload: {
    type: "",
    search: "",
    instructionTypeId: 0,
  },

  // Loading states
  loading: false,

  // Outcome management
  isAddNewOutcome: false,
  outcomeList: [],
};

const workflow = createSlice({
  name: "workflow",
  initialState,
  reducers: {
    // Node management
    setNodes: (state, action) => {
      state.nodes = action.payload;
    },
    updateNode: (state, action) => {
      const { nodeId, updates } = action.payload;
      const nodeIndex = state.nodes.findIndex((node) => node.id === nodeId);
      if (nodeIndex !== -1) {
        state.nodes[nodeIndex] = { ...state.nodes[nodeIndex], ...updates };
      }
    },
    addNode: (state, action) => {
      state.nodes.push(action.payload);
    },
    removeNode: (state, action) => {
      const nodeId = action.payload;
      state.nodes = state.nodes.filter((node) => node.id !== nodeId);
    },
    removeNodes: (state, action) => {
      const nodeIds = action.payload;
      state.nodes = state.nodes.filter((node) => !nodeIds.includes(node.id));
    },

    // Edge management
    setEdges: (state, action) => {
      state.edges = action.payload;
    },
    addEdge: (state, action) => {
      state.edges.push(action.payload);
    },
    removeEdge: (state, action) => {
      const edgeId = action.payload;
      state.edges = state.edges.filter((edge) => edge.id !== edgeId);
    },
    updateEdges: (state, action) => {
      state.edges = action.payload;
    },

    // UI state management
    setSelectedElement: (state, action) => {
      state.selectedElement = action.payload;
    },
    setOpenPanel: (state, action) => {
      state.isOpenPanel = action.payload;
    },
    setSelectedPanelData: (state, action) => {
      state.selectedPanelData = action.payload;
    },
    openPanel: (state, action) => {
      const { nodeData, nodeId } = action.payload;
      state.isOpenPanel = true;
      state.selectedPanelData = { parentNode: nodeData, parentNodeId: nodeId };
      state.selectedElement = nodeId;
    },
    closePanel: (state) => {
      state.isOpenPanel = false;
      state.selectedElement = null;
      state.selectedPanelData = {};
    },

    // List data management
    setListData: (state, action) => {
      state.listData = { ...state.listData, ...action.payload };
    },
    updateListDataType: (state, action) => {
      const { type, data } = action.payload;
      state.listData[type] = data;
    },

    // Filter management
    setFilterPayload: (state, action) => {
      state.filterPayload = { ...state.filterPayload, ...action.payload };
    },

    // Loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Outcome management
    setIsAddNewOutcome: (state, action) => {
      state.isAddNewOutcome = action.payload;
    },
    setOutcomeList: (state, action) => {
      state.outcomeList = action.payload;
    },

    // Bulk operations for performance
    bulkUpdateNodes: (state, action) => {
      const updates = action.payload; // Array of { id, updates }
      updates.forEach(({ id, updates: nodeUpdates }) => {
        const nodeIndex = state.nodes.findIndex((node) => node.id === id);
        if (nodeIndex !== -1) {
          state.nodes[nodeIndex] = {
            ...state.nodes[nodeIndex],
            ...nodeUpdates,
          };
        }
      });
    },

    // Reset state
    resetWorkflowState: (state) => {
      return { ...initialState };
    },
  },
});

export const {
  setNodes,
  updateNode,
  addNode,
  removeNode,
  removeNodes,
  setEdges,
  addEdge,
  removeEdge,
  updateEdges,
  setSelectedElement,
  setSelectedPanelData,
  setOpenPanel,
  openPanel,
  closePanel,
  setListData,
  updateListDataType,
  setFilterPayload,
  setLoading,
  setIsAddNewOutcome,
  setOutcomeList,
  bulkUpdateNodes,
  resetWorkflowState,
} = workflow.actions;

export default workflow.reducer;

// Selectors for performance
export const Nodes = (state) => state.workflow?.nodes;
export const Edges = (state) => state.workflow?.edges;
export const selectSelectedElement = (state) => state.workflow.selectedElement;
export const selectIsOpenPanel = (state) => state.workflow.isOpenPanel;
export const selectSelectedPanelData = (state) =>
  state.workflow.selectedPanelData;
export const TaskMileStoneOutcomeList = (state) => state.workflow?.listData;
export const selectFilterPayload = (state) => state.workflow.filterPayload;
export const selectLoading = (state) => state.workflow.loading;
export const selectOutcomeList = (state) => state.workflow.outcomeList;
export const selectIsAddNewOutcome = (state) => state.workflow.isAddNewOutcome;
